import { useState } from 'react';
import api from '../../hooks/useApi';
import { formatDate } from '../../utils/format';

// Where one contract's days went, invoice by invoice. Sits under the drawdown
// bar on an External Resource's contract card and loads when opened, so the
// contract list stays one request however many invoices a contract carries.

const INVOICE_STATUS = {
  submitted:    { label: 'Submitted',    cls: 'bg-blue-100 text-blue-700' },
  under_review: { label: 'Under review', cls: 'bg-amber-100 text-amber-700' },
  approved:     { label: 'Approved',     cls: 'bg-green-100 text-green-700' },
  paid:         { label: 'Paid',         cls: 'bg-purple-100 text-purple-700' },
};

const WEEK_STATUS = {
  approved: { label: 'Approved', cls: 'text-green-700' },
  pending:  { label: 'Pending approval', cls: 'text-amber-700' },
};

const DAYS = [['mon', 'Mon'], ['tue', 'Tue'], ['wed', 'Wed'], ['thu', 'Thu'], ['fri', 'Fri'], ['sat', 'Sat'], ['sun', 'Sun']];

const fmtDays = (n) => `${+(+n || 0).toFixed(2)}d`;

function WeekRow({ w }) {
  const st = WEEK_STATUS[w.approval_status] || { label: w.approval_status, cls: 'text-gray-500' };
  return (
    <tr className="text-gray-500">
      <td className="pl-4 pr-2 py-0.5 whitespace-nowrap">w/c {formatDate(w.week_start)}</td>
      <td className="px-2 py-0.5" colSpan={2}>
        {DAYS.filter(([k]) => w.by_day[k] > 0).map(([k, label]) => `${label} ${+w.by_day[k]}`).join(' · ')}
      </td>
      <td className="px-2 py-0.5 text-right tabular-nums">{fmtDays(w.days)}</td>
      <td className={`px-2 py-0.5 whitespace-nowrap ${st.cls}`}>{st.label}</td>
    </tr>
  );
}

export default function ContractDaysBreakdown({ workerId, contractId }) {
  const [open, setOpen]       = useState(false);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (!next) return;
    setLoading(true);
    setError('');
    api.get(`resource-workers/${workerId}/contracts/${contractId}/days`)
      .then(d => setData(d))
      .catch(e => setError(e?.message || 'Could not load the days breakdown.'))
      .finally(() => setLoading(false));
  };

  let runningTotal = +data?.opening_days || 0;
  const unbilledDays = (data?.unbilled || []).reduce((s, w) => s + w.days, 0);

  return (
    <div className="mt-2">
      <button onClick={toggle} aria-expanded={open}
        className="text-[8px] text-gold hover:underline font-semibold">
        {open ? 'Hide days by invoice ▴' : 'Days by invoice ▾'}
      </button>
      {open && (
        <div className="mt-1.5 border border-gray-100 rounded bg-gray-50 p-2">
          {loading && <div className="text-[9px] text-gray-400">Loading…</div>}
          {error && <div role="alert" className="text-[9px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">{error}</div>}
          {!loading && !error && data && (
            (data.invoices.length === 0 && data.unbilled.length === 0 && !(+data.opening_days > 0)) ? (
              <div className="text-[9px] text-gray-400 italic">No invoices or timesheet days against this contract yet.</div>
            ) : (
              <table className="w-full text-[8px]">
                <thead>
                  <tr className="text-gray-400 uppercase tracking-wide text-left">
                    <th className="pr-2 py-0.5 font-bold">Invoice</th>
                    <th className="px-2 py-0.5 font-bold">Period</th>
                    <th className="px-2 py-0.5 font-bold text-right">Invoiced</th>
                    <th className="px-2 py-0.5 font-bold text-right">Timesheets</th>
                    <th className="px-2 py-0.5 font-bold">Used to date</th>
                  </tr>
                </thead>
                <tbody>
                  {+data.opening_days > 0 && (
                    <tr className="border-t border-gray-200">
                      <td className="pr-2 py-1 font-semibold text-gray-700" colSpan={2}>Pre-system (opening balance)</td>
                      <td className="px-2 py-1 text-right tabular-nums font-semibold text-gray-700">{fmtDays(data.opening_days)}</td>
                      <td className="px-2 py-1" />
                      <td className="px-2 py-1 text-gray-500 tabular-nums">{fmtDays(runningTotal)}{data.agreed_days ? ` of ${data.agreed_days}` : ''}</td>
                    </tr>
                  )}
                  {data.invoices.map(inv => {
                    runningTotal += +inv.days_billed || 0;
                    const st = INVOICE_STATUS[inv.status] || { label: inv.status, cls: 'bg-gray-100 text-gray-600' };
                    const hasPeriod = inv.period_from && inv.period_to;
                    const mismatch = hasPeriod && Math.abs(inv.timesheet_days - inv.days_billed) > 0.001;
                    return [
                      <tr key={inv.id} className="border-t border-gray-200">
                        <td className="pr-2 py-1 whitespace-nowrap">
                          <span className="font-semibold text-navy">{inv.invoice_number || 'No number'}</span>
                          <span className={`ml-1.5 text-[7px] px-1 py-0.5 rounded font-semibold ${st.cls}`}>{st.label}</span>
                          {inv.invoice_date && <div className="text-[7px] text-gray-400">Dated {formatDate(inv.invoice_date)}</div>}
                        </td>
                        <td className="px-2 py-1 text-gray-600 whitespace-nowrap">
                          {hasPeriod ? `${formatDate(inv.period_from)} to ${formatDate(inv.period_to)}` : <span className="italic text-gray-400">No period recorded</span>}
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums font-semibold text-gray-700">{fmtDays(inv.days_billed)}</td>
                        <td className={`px-2 py-1 text-right tabular-nums ${mismatch ? 'text-amber-700 font-semibold' : 'text-gray-500'}`}
                          title={mismatch ? `Timesheets in this period total ${fmtDays(inv.timesheet_days)}; the invoice bills ${fmtDays(inv.days_billed)}` : undefined}>
                          {hasPeriod ? `${fmtDays(inv.timesheet_days)}${mismatch ? ' ⚠' : ''}` : '-'}
                        </td>
                        <td className="px-2 py-1 text-gray-500 tabular-nums">{fmtDays(runningTotal)}{data.agreed_days ? ` of ${data.agreed_days}` : ''}</td>
                      </tr>,
                      ...inv.weeks.map(w => <WeekRow key={`${inv.id}-${w.week_start}`} w={w} />),
                      (hasPeriod && inv.weeks.length === 0) ? (
                        <tr key={`${inv.id}-none`}><td colSpan={5} className="pl-4 py-0.5 text-gray-400 italic">No timesheet days recorded in this period.</td></tr>
                      ) : null,
                    ];
                  })}
                  {data.unbilled.length > 0 && [
                    <tr key="unbilled" className="border-t border-gray-200">
                      <td className="pr-2 py-1 font-semibold text-gray-700" colSpan={3}>Not yet invoiced</td>
                      <td className="px-2 py-1 text-right tabular-nums font-semibold text-gray-700">{fmtDays(unbilledDays)}</td>
                      <td className="px-2 py-1" />
                    </tr>,
                    ...data.unbilled.map(w => <WeekRow key={`unbilled-${w.week_start}`} w={w} />),
                  ]}
                </tbody>
              </table>
            )
          )}
        </div>
      )}
    </div>
  );
}
